import { useCallback, useEffect, useMemo, useState } from 'react';
import { AiOutlinePrinter } from 'react-icons/ai';
import { FaFilter } from 'react-icons/fa';
import { MdAttachMoney, MdPointOfSale } from 'react-icons/md';

import {
  DataTable,
  StyledButton,
  StyledDateInput,
  StyledSelect,
} from '../../../../components';
import { ExportButton } from '../../../../components/ExportButton';
import { useFilters } from '../../../../services/contexts/FiltersContext';
import { useUserDocument } from '../../../../services/contexts/UserDocumentContext';
import { PrintComponent } from '../../../../services/PrintComponent';
import { useVendas } from '../../../../services/hooks/useVendas';
import { CnpjMask } from '../../../../utils/CnpjMask';
import { CpfMask } from '../../../../utils/CpfMask';
import { ThityDaysAgo } from '../../../../utils/ThityDaysAgo';
import { ThityDaysFromNow } from '../../../../utils/ThityDaysFromNow';
import { AdminTitle } from '../../components';
import { Card } from './components';
import {
  CardsContainer,
  FiltersContainer,
  Row,
  VendasContainer,
} from './styles';
import { InputDescription } from '../../components/InputDescription';

const tableHead = [
  'Data e Hora (Servidor)',
  'Tipo de Produto',
  'Valor',
  'Bandeira',
  'Situação (PayStore)',
  'Autorização',
  'NSU',
  'Cartão',
  'Terminal',
  'Parcelas',
  'Documento',
  'Nome Fantasia',
];

export const Vendas = () => {
  const { documentSelected } = useUserDocument();
  const {
    initialDate,
    setInitialDate,
    finalDate,
    setFinalDate,
    productType,
    setProductType,
    flag,
    setFlag,
    cardFlagsOptions,
  } = useFilters();

  const docto = useMemo(() => documentSelected as string, [documentSelected]);

  const today = new Date();

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const [print, setPrint] = useState(false);

  const [params, setParams] = useState({
    docto: '',
    dtInicial: initialDate
      ?.toLocaleDateString('pt-BR')
      .split('/')
      .reverse()
      .join('-'),
    dtFinal: finalDate
      ?.toLocaleDateString('pt-BR')
      .split('/')
      .reverse()
      .join('-'),
    pagina: page,
    limite: itemsPerPage,
    bandeira: flag,
    tipoProd: productType,
  });

  useEffect(() => {
    if (docto) {
      setParams((previousState) => ({ ...previousState, docto: docto }));
    }
  }, [docto]);

  const { data, isLoading, isFetching, error } = useVendas({
    ...params,
    pagina: page,
    limite: itemsPerPage,
  });

  const handleFilter = useCallback(() => {
    const newParams = {
      docto: docto,
      dtInicial: initialDate
        ?.toLocaleDateString('pt-BR')
        .split('/')
        .reverse()
        .join('-'),
      dtFinal: finalDate
        ?.toLocaleDateString('pt-BR')
        .split('/')
        .reverse()
        .join('-'),
      pagina: page,
      limite: itemsPerPage,
      bandeira: flag,
      tipoProd: productType,
    };

    setParams(newParams);

    setPage(1);
    setItemsPerPage(10);
  }, [docto, initialDate, finalDate, page, itemsPerPage, flag, productType]);

  useEffect(() => {
    setPage(1);
  }, [itemsPerPage]);

  return (
    <VendasContainer>
      <AdminTitle title="Vendas" icon={<MdPointOfSale />} />
      <CardsContainer>
        <Card title="Vendas" icon={<MdAttachMoney />} params={params} />
        {/* <Card title="Não Processadas" value="R$ 52.322,00" icon={<MdBlock />} /> */}
      </CardsContainer>

      <FiltersContainer>
        <div>
          <InputDescription placeholder="Data inicial" />
          <StyledDateInput
            value={initialDate}
            onChange={setInitialDate}
            placeholder="Data inicial"
            minDate={finalDate ? ThityDaysAgo(finalDate) : undefined}
            maxDate={finalDate || undefined}
          />
        </div>

        <div>
          <InputDescription placeholder="Data final" />
          <StyledDateInput
            value={finalDate}
            onChange={setFinalDate}
            placeholder="Data final"
            minDate={initialDate || undefined}
            maxDate={initialDate ? ThityDaysFromNow(initialDate) : today}
          />
        </div>

        <div>
          <InputDescription placeholder="Filtro por bandeira" />
          <StyledSelect
            placeholder="Bandeira do cartão"
            options={cardFlagsOptions}
            setValue={setFlag}
            value={flag}
          />
        </div>

        <div>
          <InputDescription placeholder="Filtro por produto" />
          <StyledSelect
            placeholder="Tipo de Produto"
            options={[
              { id: 'DB', value: 'Débito' },
              { id: 'CV', value: 'Crédito à Vista' },
              { id: 'CP', value: 'Crédito Parcelado' },
              { id: 'PX', value: 'Pix' },
            ]}
            setValue={setProductType}
            value={productType}
          />
        </div>

        <StyledButton
          text="Filtrar"
          iconPosition="right"
          icon={<FaFilter />}
          onClick={handleFilter}
        />

        <ExportButton
          params={params}
          transactionType="transacao"
          disabled={isLoading || isFetching}
        />

        <StyledButton
          text="Imprimir"
          icon={<AiOutlinePrinter />}
          iconPosition="right"
          onClick={() => setPrint(true)}
          disabled={isLoading || isFetching}
        />
      </FiltersContainer>

      <PrintComponent onPrint={print} setOnPrint={setPrint}>
        <DataTable
          title="Detalhado"
          heads={tableHead}
          hasPagination
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          page={page}
          setPage={setPage}
          totalItems={data?.total}
        >
          <tbody>
            {isLoading || isFetching ? (
              <Row>
                <td colSpan={11}>Carregando...</td>
              </Row>
            ) : error ? (
              <Row>
                <td colSpan={11}>Falha ao obter dados</td>
              </Row>
            ) : data?.transactions.length === 0 ? (
              <Row>
                <td colSpan={11}>Não foram encontrados dados.</td>
              </Row>
            ) : (
              data?.transactions?.map((transaction) => (
                <Row key={transaction.codAutTransacao}>
                  <td>
                    {new Date(transaction.dtHrTransacao)?.toLocaleString(
                      'pt-BR'
                    )}
                  </td>
                  {transaction.tipoProd === 'CP' && (
                    <td style={{ textTransform: 'none' }}>Crédito Parcelado</td>
                  )}
                  {transaction.tipoProd === 'CV' && (
                    <td style={{ textTransform: 'none' }}>Crédito à Vista</td>
                  )}
                  {transaction.tipoProd === 'DB' && (
                    <td style={{ textTransform: 'none' }}>Débito</td>
                  )}
                  {transaction.tipoProd === 'PX' && (
                    <td style={{ textTransform: 'none' }}>Pix</td>
                  )}
                  <td>
                    {transaction.vlTransacao?.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td>{transaction.bandeira}</td>
                  <td>
                    <div
                      className={
                        transaction.situacao === 'A'
                          ? 'confirmButton'
                          : 'declinedButton'
                      }
                    >
                      {transaction.situacao === 'A' ? 'Confirmada' : 'Negada'}
                    </div>
                  </td>
                  <td>{transaction.codAutTransacao}</td>
                  <td>{transaction.nsuTransacao}</td>
                  <td>
                    {`${transaction.cartaoBIN.slice(
                      0,
                      4
                    )} ${transaction.cartaoBIN.slice(4)}XX XXXX ${transaction.cartaoHolder
                      }`}
                  </td>
                  <td>{transaction.terminal}</td>
                  <td>{transaction.qtdParcela}</td>
                  <td>
                    {transaction.estabDocto?.length === 14
                      ? CnpjMask(transaction.estabDocto)
                      : CpfMask(transaction.estabDocto)}
                  </td>
                  <td>{transaction.estabFantasia}</td>
                </Row>
              ))
            )}
          </tbody>
        </DataTable>
      </PrintComponent>
    </VendasContainer>
  );
};
